import { useCallback, useEffect, useMemo, useRef } from 'react';
import { FormProvider, useForm, useFormContext, useWatch } from 'react-hook-form';
import { CellContext } from '@tanstack/react-table';

import { useNavigationContext } from 'contexts/SchemableFormNavigationContext/SchemableFormNavigationContext';
import { WebEDIAnnotation } from 'services/webediErrors/types';
import { useWebEDIStore } from 'stores/webedi/webedi';
import { cn } from 'support/helpers/cn/cn';
import { WebEDIRowData } from 'support/types/webediTable';
import { SuggestedContentIcon } from 'components/DataTable/cells/WebEDI/components/SuggestedContentIcon/SuggestedContentIcon';
import { Dropdown } from 'components/Form/Dropdown/Dropdown';

import { useDataTableActions } from '../../../contexts/DataTableContext';
import { shouldShowDiff } from '../../../helpers/shouldShowDiff';
import { CellProps } from '../../types';
import {
  cascadeTransportUnitToChildren,
  getCellNestedLevel,
  isProcurosAddedValue,
  useCellClassNames,
  useIsReadonly,
  useLineItemsTransportUnitsConfig,
} from '../cellHelpers';
import { DropdownCellReadOnly, DropdownCellReadOnlyProps } from './DropdownCellReadOnly';

type DropdownCellProps = {
  options: Array<{
    label: string;
    value: string | number | boolean;
  }>;
};
export type Props = CellContext<WebEDIRowData, unknown> & Omit<CellProps, 'register'> & DropdownCellProps;

export const DropdownCell = (props: Props) => {
  const { fieldId, column, table, row, isHighlighted, mode, options, diffStatus } = props;
  const { columnDef } = column;
  const isValidating = useWebEDIStore((state) => state.isValidating);
  const isSubmitting = table.options.meta?.isSubmitting ?? false;
  const isAdded = isProcurosAddedValue(row);
  const { isReadOnly: isTransportUnitReadOnly, isUnassigned: isUnassignedItemTransportUnit } =
    useLineItemsTransportUnitsConfig(fieldId, row?.original, Boolean(columnDef.meta?.packingListEnabled));

  const disabled = isSubmitting;

  const level = getCellNestedLevel(row);
  const isNested = level > 1;
  const { service, NavigableItemPopoverComponent, useNavigableItemPopover } = useNavigationContext<WebEDIAnnotation>();
  const fieldState = service.useFieldStateFromAnnotations(fieldId);

  const isReadOnly = useIsReadonly({
    isAdded,
    mode,
    columnMeta: columnDef.meta,
    rowIndex: row.index,
    fieldId,
    hasError: fieldState === 'error',
  });
  const readOnly = isTransportUnitReadOnly || isReadOnly;
  const popoverItem = service.useItemByFieldPath(fieldId);
  const setCurrentItem = service.setCurrentItem;
  const { showNavigableItemPopover, setShowNavigableItemPopover, floatingStyles, refs } = useNavigableItemPopover();

  useEffect(() => {
    if (popoverItem && showNavigableItemPopover) {
      setCurrentItem(popoverItem);
    }
  }, [popoverItem, setCurrentItem, showNavigableItemPopover]);

  const onFocus = useCallback(() => {
    setShowNavigableItemPopover(true);
  }, [setShowNavigableItemPopover]);

  const onBlur = useCallback(() => {
    setShowNavigableItemPopover(false);
  }, [setShowNavigableItemPopover]);

  const cellClassNames = useCellClassNames({
    row,
    table,
    isFirstColumn: column.getIsFirstColumn(),
    isLastColumn: column.getIsLastColumn(),
  });

  const value = useWatch({ name: fieldId });
  const previousValue = useRef<string | number | boolean | undefined>(value);
  const { setValue } = useDataTableActions();
  const { getValues } = useFormContext();
  useEffect(() => {
    const isEmpty = value === undefined || value === null || value === '';
    // A legitimately unset field starts (and can stay) `null` — that's a real value, not
    // something to restore over. Only fall back to `previousValue` when it holds an actual value
    // itself; otherwise every mount of a null field would re-`setValue` it right back to `null`.
    const previousValueIsReal = previousValue.current !== undefined && previousValue.current !== null;
    if (isEmpty && previousValueIsReal) {
      setValue(fieldId, previousValue.current);
    }
  }, [value, previousValue, setValue, fieldId]);

  useEffect(() => {
    let isCancelled = false;
    if (isModificationType(fieldId) && !value && !previousValue.current && options.length > 0) {
      setTimeout(() => {
        if (!isCancelled) {
          previousValue.current = options[0].value as string;
          setValue(fieldId, options[0].value);
        }
      }, 100);
    }

    return () => {
      isCancelled = true;
    };
  }, [value, fieldId, options, setValue]);

  const hasDiff = shouldShowDiff(diffStatus, fieldState);

  if (readOnly) {
    return (
      <DropdownReadOnly
        cellProps={props}
        fieldId={fieldId}
        options={options}
        isNotShippedItemTransportUnit={isTransportUnitReadOnly}
        isUnassignedItemTransportUnit={isUnassignedItemTransportUnit}
      />
    );
  }

  return (
    <div
      ref={refs.setReference}
      className={cn(
        'flex size-full items-center border p-1.5',
        {
          'bg-amber-100': isHighlighted && !hasDiff,
          'bg-red-50 border-transparent': fieldState === 'error',
          'bg-blue-50 border-transparent': fieldState === 'pending-validation',
          'bg-sky-50 border-transparent focus-within:border-sky-500': hasDiff,
          'border-transparent': !hasDiff && fieldState !== 'error' && fieldState !== 'pending-validation',
        },
        cellClassNames,
      )}
    >
      <Dropdown
        disabled={disabled || (columnDef.meta?.disableIfNested && isNested)}
        onChange={(value) => {
          if (value !== undefined && value !== null && value !== '') {
            previousValue.current = value;
            setValue(fieldId, value, {
              shouldDirty: true,
            });
            // When a main item's transport unit changes, cascade to its sub-items.
            // Sub-items have disabledIfNested=true so they can't be changed directly.
            // Use getValues to read directly from RHF state (always current, not debounced).
            if (!isNested && fieldId.includes('._internal.parent_transport_unit')) {
              const prefixMatch = fieldId.match(/^(.+)\.\d+\._internal\.parent_transport_unit$/);
              if (prefixMatch) {
                const allItems = getValues(prefixMatch[1]) as Array<WebEDIRowData>;
                cascadeTransportUnitToChildren(fieldId, value, allItems, setValue);
              }
            }
          }
        }}
        value={value ?? previousValue.current}
        onFocus={onFocus}
        onOpen={onFocus}
        onBlur={onBlur}
        options={options}
        fieldState={fieldState}
        name={fieldId}
        size="extra-small"
        buttonClassName="rounded-3xl"
        wrapperClassName="flex-1"
        rightIcon={hasDiff ? <SuggestedContentIcon mode="edit" /> : undefined}
      />
      {showNavigableItemPopover && NavigableItemPopoverComponent && (
        <NavigableItemPopoverComponent
          annotation={popoverItem}
          viewMode={false}
          ref={refs.setFloating}
          style={floatingStyles}
          isValidating={isValidating}
          isPendingValidation={fieldState === 'pending-validation'}
          fieldDescription={columnDef.meta?.description}
        />
      )}
    </div>
  );
};

const useViewForm = ({ fieldId, options }: { fieldId: string; options: DropdownCellProps['options'] }) => {
  const value = useWatch({ name: fieldId });
  const valueForReadonly = useMemo(() => {
    return options?.find((option) => option.value === value)?.label;
  }, [value, options]);

  return useForm({ values: { [fieldId]: valueForReadonly } });
};

const DropdownReadOnly = ({
  fieldId,
  options,
  cellProps,
  isNotShippedItemTransportUnit,
  isUnassignedItemTransportUnit,
}: {
  fieldId: string;
  options: DropdownCellProps['options'];
  cellProps: Props;
  isNotShippedItemTransportUnit: DropdownCellReadOnlyProps['isNotShippedItemTransportUnit'];
  isUnassignedItemTransportUnit: DropdownCellReadOnlyProps['isUnassignedItemTransportUnit'];
}) => {
  const formMethods = useViewForm({ fieldId, options });
  return (
    <FormProvider {...formMethods}>
      <DropdownCellReadOnly
        {...cellProps}
        isNotShippedItemTransportUnit={isNotShippedItemTransportUnit}
        isUnassignedItemTransportUnit={isUnassignedItemTransportUnit}
      />
    </FormProvider>
  );
};

//in case it is type of modification, set by default the first value
const isModificationType = (fieldId: string | undefined) => {
  return fieldId?.includes('.type') && fieldId?.includes('modification_groups');
};
